import {
  BUILT_IN_SOURCE_LOCALE,
  INSTALLABLE_LOCALES,
  LOCALE_DEFINITIONS,
  getLocaleDefinition,
  type InstallableLocale,
} from "./preparedLocales";

export const LOCALE_CONFIGURATION_CONTRACT = "kavoosh-locale-configuration.v1" as const;
export const SHARED_CATALOG_VERSION = "kavoosh-shared-ui.v1" as const;
export const PLATFORM_DEFAULT_LOCALE = BUILT_IN_SOURCE_LOCALE;
export const FALLBACK_LOCALE = BUILT_IN_SOURCE_LOCALE;
export const ACTIVE_LOCALE_CHANGE_EVENT = "kavoosh:locale-change" as const;

export type LocaleConfigurationRecord = {
  kind: string;
  key: string;
  version: number;
  status: string;
  payload: Record<string, unknown>;
};

export type LocaleInventoryItem = {
  locale: InstallableLocale;
  label: string;
  englishLabel: string;
  languageTag: string;
  direction: "ltr" | "rtl";
  installed: boolean;
  enabled: boolean;
  isDefault: boolean;
  source: "built_in" | "admin_record" | "prepared";
  configurationVersion: number | null;
};

export type RuntimeLocaleResolution = {
  locale: InstallableLocale;
  source: "explicit" | "user" | "tenant" | "browser" | "fallback";
};

export function isInstallableLocale(value: unknown): value is InstallableLocale {
  return typeof value === "string" && (INSTALLABLE_LOCALES as readonly string[]).includes(value.toLowerCase());
}

export function localeConfigurationKey(locale: InstallableLocale): string {
  return `locale-${locale}`;
}

export function localeConfigurationPayload(locale: InstallableLocale, enabled: boolean): Record<string, unknown> {
  if (locale === PLATFORM_DEFAULT_LOCALE) {
    throw new Error("English is the built-in platform default and cannot be installed or disabled.");
  }
  const definition = getLocaleDefinition(locale);
  return {
    contract_version: LOCALE_CONFIGURATION_CONTRACT,
    locale,
    installed: true,
    enabled,
    default_locale: false,
    fallback_locale: FALLBACK_LOCALE,
    direction: definition.direction,
    language_tag: definition.languageTag,
    technical_direction: definition.technicalDirection,
    shared_catalog_version: SHARED_CATALOG_VERSION,
  };
}

function validLocaleConfigurationRecord(
  record: LocaleConfigurationRecord,
): record is LocaleConfigurationRecord & { payload: Record<string, unknown> & { locale: InstallableLocale } } {
  if (record.kind !== "locale_configuration" || record.status !== "active") return false;
  const locale = record.payload.locale;
  if (!isInstallableLocale(locale) || locale === PLATFORM_DEFAULT_LOCALE) return false;
  const definition = getLocaleDefinition(locale);
  return record.key === localeConfigurationKey(locale)
    && record.payload.contract_version === LOCALE_CONFIGURATION_CONTRACT
    && record.payload.installed === true
    && typeof record.payload.enabled === "boolean"
    && record.payload.default_locale === false
    && record.payload.fallback_locale === FALLBACK_LOCALE
    && record.payload.direction === definition.direction
    && record.payload.language_tag === definition.languageTag
    && record.payload.technical_direction === definition.technicalDirection
    && record.payload.shared_catalog_version === SHARED_CATALOG_VERSION;
}

export function buildLocaleInventory(records: readonly LocaleConfigurationRecord[]): LocaleInventoryItem[] {
  const latest = new Map<InstallableLocale, LocaleConfigurationRecord>();
  for (const record of records) {
    if (!validLocaleConfigurationRecord(record)) continue;
    const locale = record.payload.locale;
    const current = latest.get(locale);
    if (!current || record.version > current.version) latest.set(locale, record);
  }

  return INSTALLABLE_LOCALES.map((locale) => {
    const definition = LOCALE_DEFINITIONS[locale];
    if (locale === PLATFORM_DEFAULT_LOCALE) {
      return {
        ...definition,
        installed: true,
        enabled: true,
        isDefault: true,
        source: "built_in" as const,
        configurationVersion: null,
      };
    }
    const record = latest.get(locale);
    if (!record) {
      return {
        ...definition,
        installed: false,
        enabled: false,
        isDefault: false,
        source: "prepared" as const,
        configurationVersion: null,
      };
    }
    return {
      ...definition,
      installed: true,
      enabled: record.payload.enabled === true,
      isDefault: false,
      source: "admin_record" as const,
      configurationVersion: record.version,
    };
  });
}

function installableLocaleForBrowserTag(tag: string): InstallableLocale | null {
  const normalized = tag.trim().toLowerCase();
  if (!normalized) return null;
  for (const locale of INSTALLABLE_LOCALES) {
    const definition = LOCALE_DEFINITIONS[locale];
    if (definition.languageTag.toLowerCase() === normalized) return locale;
    if (definition.languageTag.toLowerCase().split("-")[0] === normalized.split("-")[0]) return locale;
  }
  return null;
}

export function resolveRuntimeLocale(input: {
  enabledLocales: readonly InstallableLocale[];
  explicitLocale?: string | null;
  userLocale?: string | null;
  tenantLocale?: string | null;
  browserLanguages?: readonly string[];
}): RuntimeLocaleResolution {
  const enabled = new Set<InstallableLocale>([PLATFORM_DEFAULT_LOCALE, ...input.enabledLocales]);
  const directCandidates: Array<[RuntimeLocaleResolution["source"], string | null | undefined]> = [
    ["explicit", input.explicitLocale],
    ["user", input.userLocale],
    ["tenant", input.tenantLocale],
  ];
  for (const [source, candidate] of directCandidates) {
    const normalized = typeof candidate === "string" ? candidate.toLowerCase() : candidate;
    if (isInstallableLocale(normalized) && enabled.has(normalized)) return { locale: normalized, source };
  }
  for (const browserTag of input.browserLanguages ?? []) {
    const locale = installableLocaleForBrowserTag(browserTag);
    if (locale && enabled.has(locale)) return { locale, source: "browser" };
  }
  return { locale: FALLBACK_LOCALE, source: "fallback" };
}

export function activeLocaleFromDocument(): InstallableLocale {
  if (typeof document === "undefined") return FALLBACK_LOCALE;
  const candidate = document.documentElement.dataset.kavooshLocale;
  return isInstallableLocale(candidate) ? candidate : FALLBACK_LOCALE;
}

export function applyRuntimeLocale(locale: InstallableLocale): void {
  const definition = getLocaleDefinition(locale);
  const root = document.documentElement;
  const changed = root.dataset.kavooshLocale !== locale
    || root.lang !== definition.languageTag
    || root.dir !== definition.direction;
  root.dataset.kavooshLocale = locale;
  root.lang = definition.languageTag;
  root.dir = definition.direction;
  if (changed) window.dispatchEvent(new Event(ACTIVE_LOCALE_CHANGE_EVENT));
}
